/** Niveles 0–100 por canal (porcentaje del volumen base). */
export interface MixVolumes {
  boom: number;
  drums: number;
  harmony: number;
}

export const DEFAULT_MIX_VOLUMES: MixVolumes = {
  boom: 90,
  drums: 88,
  harmony: 88,
};

const STORAGE_KEY = 'boom-mix-volumes';

const BASE_GAIN = {
  boom: 0.9,
  drums: 0.88,
  harmony: 2.35,
} as const;

let activeVolumes: MixVolumes = { ...DEFAULT_MIX_VOLUMES };

export function loadMixVolumes(): MixVolumes {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_MIX_VOLUMES };
    const parsed = JSON.parse(raw) as MixVolumes;
    return clampMixVolumes(parsed);
  } catch {
    return { ...DEFAULT_MIX_VOLUMES };
  }
}

export function saveMixVolumes(volumes: MixVolumes): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(clampMixVolumes(volumes)));
}

function clampMixVolumes(volumes: MixVolumes): MixVolumes {
  const clamp = (n: number) => Math.min(100, Math.max(0, Math.round(n)));
  return {
    boom: clamp(volumes.boom ?? DEFAULT_MIX_VOLUMES.boom),
    drums: clamp(volumes.drums ?? DEFAULT_MIX_VOLUMES.drums),
    harmony: clamp(volumes.harmony ?? DEFAULT_MIX_VOLUMES.harmony),
  };
}

export function channelGain(channel: keyof MixVolumes, volumes: MixVolumes = activeVolumes): number {
  return BASE_GAIN[channel] * (volumes[channel] / 100);
}

export function getBoomChannelGain(): number {
  return channelGain('boom');
}

export function getDrumsChannelGain(): number {
  return channelGain('drums');
}

export function getHarmonyChannelGain(): number {
  return channelGain('harmony');
}

export function getActiveMixVolumes(): MixVolumes {
  return { ...activeVolumes };
}

export function applyMixVolumes(volumes: MixVolumes): MixVolumes {
  activeVolumes = clampMixVolumes(volumes);
  saveMixVolumes(activeVolumes);
  return activeVolumes;
}

applyMixVolumes(loadMixVolumes());
